import { SIZING_FRACTION } from '@postflop/schema';
/** ±2% of the true amount → "exact"; ±10% → "close"; beyond → "off" (§7.3). */
export const EXACT_TOLERANCE = 0.02;
export const CLOSE_TOLERANCE = 0.1;
/**
 * The true dollar amount a sizing intent means at this pot (§7.3). `SIZING_FRACTION[intent] × pot`.
 * For a RAISE the convention is pot-AFTER-CALL: the pot the bet is sized against already includes
 * hero's call of the current bet (documented here as the single source of that convention).
 *
 *   bet   (toCall = 0):  amount = fraction × pot
 *   raise (toCall > 0):  amount = call + fraction × (pot + call)   [pot-after-call, then raise on top]
 *
 * `min` and `jam` are context-computed (min-raise / all-in), not a fixed fraction - the caller must
 * pass their true amount via `overrideExpected`; absent that we return null (ungradeable arithmetic).
 */
export const expectedAmount = (intent, pot, toCall = 0, overrideExpected) => {
    if (intent === 'min' || intent === 'jam')
        return overrideExpected ?? null;
    const fraction = SIZING_FRACTION[intent];
    if (toCall > 0) {
        const potAfterCall = pot + toCall;
        return toCall + fraction * potAfterCall; // call the bet, then bet `fraction` of the resulting pot.
    }
    return fraction * pot;
};
/** Grade a typed amount against an expected true amount. Pure arithmetic (§7.3). */
export const gradeArithmeticAmount = (typed, expected) => {
    // a non-positive expected amount is degenerate; treat an exact-zero match as exact, else off.
    if (expected <= 0) {
        const verdict = typed === expected ? 'exact' : 'off';
        return { verdict, expected, typed, relError: 0 };
    }
    const relError = (typed - expected) / expected;
    const mag = Math.abs(relError);
    const verdict = mag <= EXACT_TOLERANCE ? 'exact' : mag <= CLOSE_TOLERANCE ? 'close' : 'off';
    return { verdict, expected, typed, relError };
};
/**
 * Grade the user's typed amount for a chosen intent at this pot. Returns null when the intent is not
 * fraction-based and no override amount is supplied (nothing to grade against), so the caller can skip
 * arithmetic scoring rather than fake a verdict.
 */
export const gradeBetMath = (intent, typed, pot, toCall = 0, overrideExpected) => {
    const expected = expectedAmount(intent, pot, toCall, overrideExpected);
    if (expected === null)
        return null;
    return gradeArithmeticAmount(typed, expected);
};
//# sourceMappingURL=bet-math.js.map